import React, { Component, Fragment } from "react"
import ReactPlayer from "react-player/lazy"
import { bindActionCreators, compose } from "redux"
import { connect } from "react-redux"
import cx from "classnames"

import {
  startInactivityTimeout,
  stopInactivityTimeout,
  setShowIdleModal,
} from "store/user/actions"

import { isIOSDevice } from "helpers/util"

import "styles/components/common/mediaplayer.scss"

class MediaPlayer extends Component {
  state = {
    url: null,
    pip: false,
    playing: false,
    controls: false,
    light: false,
    volume: 0.8,
    muted: false,
    played: 0,
    playedSeconds: 0,
    loaded: 0,
    duration: 0,
    elapsed: 0,
    remaining: 0,
    seconds: 0,
    playbackRate: 1.0,
    loop: false,
    onError: false,
    isStopped: false,
  }

  componentDidMount() {
    // Set up an interval to check if the player is playing every 10 seconds
    this.interval = setInterval(this.checkPlayerPlaying, 10000)
  }

  componentWillUnmount() {
    // Clear the interval when the component is unmounted
    clearInterval(this.interval)
  }

  resetInactivityTimer = () => {
    const { stopInactivityTimeout } = this.props
    stopInactivityTimeout()
  }

  checkPlayerPlaying = () => {
    // Access the ReactPlayer's play state (you might need to adjust the ref)
    const { playing } = this.state

    if (playing) {
      // Dispatch the Redux action if the player is playing
      this.resetInactivityTimer()
    }
  }

  componentDidUpdate(prevProps) {
    const { sessionPaused: prevSessionPaused = false } = prevProps
    const { sessionPaused = false } = this.props

    const { playing, isStopped } = this.state

    if (sessionPaused !== prevSessionPaused) {
      this.setState({
        playing: isStopped ? !sessionPaused : false,
        isStopped: playing,
      })
    }
  }

  handlePlayPause = () => {
    const { playing } = this.state
    this.setState({ playing: !playing })
  }

  handlePlay = () => {
    this.setState({ playing: true })
  }

  handlePause = async () => {
    const { onPauseMedia = false } = this.props
    const { playing } = this.state

    if (playing) {
      await this.setState({ playing: false })

      if (onPauseMedia) {
        await onPauseMedia()
      }
    }
  }

  handleErrorLoading = (error) => {
    const { onError } = this.state
    console.log("MEDIA LOADING ERROR: ", error)
    this.setState({ onError: !onError })
  }

  handleDuration = (duration) => {
    const { type } = this.props
    this.setState({ duration })

    if (type !== "yt") {
      this.handleOnMediaReady()
    }
  }

  handleProgress = async (state) => {
    const { onProgress, onGetMediaProgress, forwardStreaming } = this.props
    const { playedSeconds } = this.state

    const delta = state.playedSeconds - playedSeconds

    if (delta >= 0) {
      if (delta <= 1.2 || forwardStreaming) {
        await this.setState(state)

        if (onGetMediaProgress) {
          await onGetMediaProgress(state)
        }
      } else {
        this.player.seekTo(playedSeconds)
      }
    }

    if (onProgress) {
      await onProgress()
    }
  }

  handleOnMediaReady = () => {
    const { lastProgress } = this.props

    if (lastProgress) {
      this.player.seekTo(lastProgress)
      this.setState({
        playedSeconds: lastProgress,
      })
    } else {
      this.player.seekTo(0.0)
    }
  }

  handleEnded = () => {
    const { onEndedMedia } = this.props

    this.setState({ playing: this.state.loop })

    if (onEndedMedia) {
      this.props.onEndedMedia()
    }
  }

  handleSeek = async () => {
    const { type, forwardStreaming } = this.props

    switch (type) {
      case "audio":
        let audio = document.getElementsByTagName("audio")[0]

        if (!forwardStreaming) {
          audio.onseeking = this.onSeeking
        }
        break

      case "video":
        let video = document.getElementsByTagName("video")[0]

        if (!forwardStreaming) {
          video.onseeking = this.onSeeking
        }
        break

      default:
        break
    }
  }

  onSeeking = () => {
    const { type } = this.props
    const { playedSeconds } = this.state

    let player = false
    try {
      player = document.getElementsByTagName(type)[0]
    } catch (err) {
      player = false
    }

    const targetTime = this.player.getCurrentTime()
    const delta = targetTime - playedSeconds

    if (player && delta > 0.01) {
      player.currentTime = playedSeconds
    }
  }

  ref = (player) => {
    this.player = player
  }

  render() {
    const {
      media,
      type,
      lastProgress,
      mediaConfig,
      forwardStreaming,
      customStyle,
    } = this.props
    const { playing, onError } = this.state

    const iOSDevice = isIOSDevice()

    return (
      <div className={`player-wrapper ${type}`} style={customStyle}>
        {onError ? (
          <div className="error-media">
            <div className="video-overlay">
              <span className="icon">
                <i className="fas fa-exclamation-triangle fa-3x"></i>
              </span>
              <span className="error-media-label">Unable to load media</span>
            </div>
          </div>
        ) : (
          <Fragment>
            {type === "audio" && (
              <div
                className="module-audio-bg"
                onClick={() => this.handlePlayPause()}
              >
                <i className="fas fa-volume-up fa-4x"></i>
              </div>
            )}
            {type && (
              <ReactPlayer
                id="player"
                ref={this.ref}
                url={media}
                controls
                className={cx("react-player")}
                width="100%"
                height="100%"
                playing={playing}
                playsinline={iOSDevice ? true : false}
                onPlay={this.handlePlay}
                onPause={this.handlePause}
                onError={this.handleErrorLoading}
                onProgress={this.handleProgress}
                onDuration={this.handleDuration}
                onEnded={this.handleEnded}
                onSeek={() => this.handleSeek()}
                onContextMenu={(e) => e.preventDefault()}
                config={{
                  file: {
                    attributes: {
                      controlsList: mediaConfig?.video_download
                        ? "noplaybackrate"
                        : "nodownload noplaybackrate",
                      disablePictureInPicture: true,
                    },
                  },
                  youtube: {
                    playerVars: {
                      modestbranding: 1,
                      rel: 0,
                      disablekb: 1,
                      showinfo: 0,
                      start: Math.floor(lastProgress),
                      controls: forwardStreaming ? 1 : 0,
                      fs: iOSDevice ? 0 : 1,
                    },
                  },
                  soundcloud: {
                    options: {
                      auto_play: false,
                      buying: false,
                      sharing: false,
                      download: false,
                      show_playcount: false,
                      show_user: false,
                      single_active: true,
                      start_track: lastProgress,
                    },
                    playerVars: {
                      controls: forwardStreaming ? 1 : 0,
                    },
                  },
                }}
              />
            )}
          </Fragment>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  school: state.schoolReducer,
  user: state.userReducer,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { startInactivityTimeout, stopInactivityTimeout, setShowIdleModal },
    dispatch
  )

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  MediaPlayer
)
